import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { getLanguage, suggestLanguage } from "./languageMap";
import { useTranslation } from "./useTranslation";

/**
 * LanguageSuggestionToast - first-visit only. Detects user's location via
 * IP (ipapi.co - free, no auth, returns country + region/state) and, if
 * the suggested language differs from the user's current language AND
 * they haven't dismissed before, surfaces a quiet toast offering to
 * translate the page to the local tongue.
 *
 * Persistence:
 *   - "vilora-lang"          : current chosen language (set by useTranslation)
 *   - "vilora-lang-suggest"  : "dismissed" once user dismisses or accepts
 */

const SUGGEST_KEY = "vilora-lang-suggest";
const DELAY_MS = 1400;

export default function LanguageSuggestionToast() {
  const { current, changeLanguage } = useTranslation();
  const [suggestion, setSuggestion] = useState(null); // { code, country, region }
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Already dismissed in this device
    try {
      if (window.localStorage.getItem(SUGGEST_KEY) === "1") return undefined;
    } catch {
      /* storage unavailable - proceed */
    }
    let cancelled = false;
    let timer;

    const detect = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const code = suggestLanguage({
          country: data.country_code,
          region: data.region,
        });
        if (!code || code === current || code === "en") return;
        timer = window.setTimeout(() => {
          if (!cancelled) {
            setSuggestion({
              code,
              country: data.country_name,
              region: data.region,
            });
          }
        }, DELAY_MS);
      } catch {
        /* network blocked - silent fail; user can use the picker */
      }
    };

    detect();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [current]);

  if (!suggestion) return null;

  const lang = getLanguage(suggestion.code);

  const dismiss = () => {
    try { window.localStorage.setItem(SUGGEST_KEY, "1"); } catch {}
    setClosing(true);
    window.setTimeout(() => setSuggestion(null), 280);
  };

  const accept = () => {
    try { window.localStorage.setItem(SUGGEST_KEY, "1"); } catch {}
    changeLanguage(suggestion.code);
  };

  return (
    <aside
      className={`lang-toast ${closing ? "is-closing" : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className="lang-toast-rule" aria-hidden="true" />

      <div className="lang-toast-head">
        <div className="lang-toast-folio">
          <span className="lang-toast-pulse" aria-hidden="true" />
          <span>FROM · {(suggestion.region || suggestion.country || "").toUpperCase()}</span>
        </div>
        <button
          type="button"
          className="lang-toast-close"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <X size={14} strokeWidth={1.6} />
        </button>
      </div>

      <p className="lang-toast-title">
        Read this page in <em className="display-italic">{lang.native}</em>?
      </p>
      <p className="lang-toast-deck">
        We detected you may be browsing from {suggestion.region ? `${suggestion.region}, ` : ""}
        {suggestion.country}. Translate the entire site automatically.
      </p>

      <div className="lang-toast-actions">
        <button
          type="button"
          className="lang-toast-yes"
          onClick={accept}
        >
          <span>Translate to {lang.native}</span>
          <span className="lang-toast-yes-arrow">→</span>
        </button>
        <button
          type="button"
          className="lang-toast-no"
          onClick={dismiss}
        >
          Keep English
        </button>
      </div>

      <span className="lang-toast-corner lang-toast-corner--tl" aria-hidden="true" />
      <span className="lang-toast-corner lang-toast-corner--br" aria-hidden="true" />
    </aside>
  );
}
